import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnInit, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { ExploreMoreIndustriesComponent } from '../explore-more-industries/explore-more-industries.component';
import { trigger, transition, query, style, stagger, animate } from '@angular/animations';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface CaseStudy {
  image: string;
  title: string;
  description: string;
}
@Component({
  selector: 'app-education-elearning',
  imports: [CommonModule, RouterModule, FaqComponent, ExploreMoreIndustriesComponent],
  standalone: true,
  templateUrl: './education-elearning.component.html',
  styleUrl: './education-elearning.component.scss',
  animations: [
    trigger('fadeInUpStagger', [
      transition('* => visible', [
        query('.animate-item', [
          style({ opacity: 0, transform: 'translateY(30px)' }),
          stagger('200ms', [
            animate('800ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})
export class EducationElearningComponent implements AfterViewInit, OnInit, OnDestroy {
  private jsonLdScript: any;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('impactSection') impactSection!: ElementRef;
  impactSectionVisible = false;
  private observer: IntersectionObserver | null = null;

  caseStudies: CaseStudy[] = [
    {
      image: '../../../../assets/industries/edtech/edutech1.png',
      title: 'Advanced Medical EdTech Platform for Seamless Learning',
      description: 'Our EdTech website for a medical academy offers a secure, user-friendly platform optimized for healthcare professionals seeking specialized courses. It features easy navigation, mobile responsiveness, fast loading, secure login, payment integration, live streaming, course tracking, automated notifications, and QR code attendance. Robust admin controls support course management, enrollment approvals, and analytics. SEO-optimized content and design ensure high rankings for key medical training searches, driving targeted traffic and maximizing enrollments.'
    },
    {
      image: '../../../../assets/industries/edtech/edutech2.png',
      title: 'Smarter Education through Digital Learning Management System',
      description: 'Our Digital Learning Management System (DLMS) revolutionizes the K-12 educational experience by eliminating geographical and scheduling barriers to quality education. This case study demonstrates how integrated AI-based learning, real-time progress tracking, and recorded content delivery create an ecosystem that benefits students, parents, and educators simultaneously. By addressing critical challenges in traditional education—lack of individual attention, expensive supplementary classes, and missed learning opportunities—our platform delivers an engaging, accessible learning environment where education truly comes to every home, anytime.'
    }
    // {
    //   image: '../../../../assets/industries/healthcare/case-study-healthcare.png',
    //   title: 'Unified Customer Engagement Portal',
    //   description: 'Kalpita Technologies partnered with a top-tier insurance provider to modernize their customer engagement ecosystem through a unified digital portal. The platform consolidated multiple service lines into one secure, SOC2 and GDPR compliant environment, enabling seamless client–advisor collaboration, consistent brand communication, and actionable business insights via integrated Power BI dashboards. This transformation enhanced customer retention, improved decision-making, and reinforced the client’s brand identity across all insurance verticals.'
    // }
  ];
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  edTechFaqs: FaqItem[] = [
    {
      question: 'What services do you offer for EdTech software development?',
      answer: `We provide custom LMS development, eLearning software development services, 
      mobile learning app development, online education platform development, legacy system 
      migration and cloud modernization, API integration with existing educational tools and 
      AI-powered EdTech modernization.`
    },
    {
      question: 'Can you help build a custom eLearning platform from scratch?',
      answer: `Absolutely. We specialize in end-to-end eLearning platform development, guiding 
      you from concept to launch and beyond. Our process includes:
      <ul>
        <li>Discovery & strategy workshops to define your unique value proposition</li>
        <li>UX/UI design optimized for learner engagement</li>
        <li>Agile development with biweekly demos for continuous feedback</li>
        <li>Cloud infrastructure setup for optimal performance and scalability</li>
        <li>Quality assurance and security testing</li>
        <li>Deployment, training, and post-launch support</li>
      </ul>`
    },
    {
      question: 'Do you integrate AI into education platforms?',
      answer: `Absolutely. Our AI-powered EdTech solutions transform the learning experience through:
      <ul>
        <li>Adaptive learning paths that adjust to individual student performance</li>
        <li>Automated assessment and grading systems</li>
        <li>Intelligent content recommendations based on learning patterns</li>
        <li>NLP and AI for chatbot tutors and Q&A support</li>
        <li>AI-driven content generation for quizzes, summaries, and study materials</li>
      </ul>`
    },
    {
      question: 'Are your learning platforms scalable for large audiences?',
      answer: `Yes. We architect cloud-native, secure platforms built for elastic scalability capable of supporting thousands of learners simultaneously. Our solutions:
      <ul>
        <li>Automatically scale infrastructure based on demand and support tens of thousands of concurrent users</li>
        <liMaintain consistent performance during peak enrollment periods</li>
        <li>Utilize CDN networks for global content delivery</li>
        <li>Implement load balancing and caching strategies for optimal speed</li>
        <li>Design database architectures that handle millions of learning records</li>
      </ul>
      Whether you're a startup launching your first course or an institution serving thousands globally, our platforms grow with your needs.`
    },
    {
      question: 'Do you develop mobile apps for eLearning?',
      answer: `Yes. Our eLearning mobile app development delivers engaging, accessible learning experiences on any device. We build:
      <ul>
        <li>Android, iOS, and cross-platform apps for optimal performance</li>
        <li>Offline learning capabilities with automatic synchronization</li>
        <li>Push notifications for course updates, deadlines, and achievements</li>
        <li>Interactive modules with video, quizzes, and gamification</li>
        <li>Accessibility features compliant with WCAG standards</li>
      </ul>`
    },
    {
      question: 'What is AI-accelerated software development?',
      answer: `AI-accelerated development uses artificial intelligence to speed up architecture design, 
      code generation, testing, and integration - reducing development time by 50-60% while 
      maintaining high quality standards. This approach allows businesses to launch new capabilities 
      in weeks or months rather than years.`
    }
  ];

  currentSlide = 0;
  isDragging = false;
  startPosition = 0;
  currentPosition = 0;
  transformValue = 0;
  private autoSlideInterval: any;
  private readonly SLIDE_DURATION = 5000;

  constructor(private router: Router, private modalService: NgbModal,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    this.stopAutoSlide();
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    // 4. Set up Intersection Observer
    const options = {
      root: null,
      threshold: 0.2,
      rootMargin: '0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.impactSectionVisible = true;
          this.observer?.disconnect();
        }
      });
    }, options);

    if (this.impactSection) {
      this.observer.observe(this.impactSection.nativeElement);
    }

    // 5. Initialize 3D Tilt Effect
    this.setupTiltEffect();
  }

  setupTiltEffect(): void {
    const cards = document.querySelectorAll('.solution-image-card, .impact-image-wrapper, .case-study-image-wrapper');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const xc = rect.width / 2;
        const yc = rect.height / 2;
        const maxRotate = 10;

        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;

        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out';
      };

      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  scheduleDemo(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  startAutoSlide(): void {
    this.stopAutoSlide(); // Ensure no duplicate intervals

    // MODIFIED: Only start auto-slide if there is more than 1 slide
    if (this.caseStudies.length > 1) {
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide();
      }, this.SLIDE_DURATION);
    }
  }

  stopAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
  }

  nextSlide(): void {
    if (this.currentSlide < this.caseStudies.length - 1) {
      this.currentSlide++;
    } else {
      this.currentSlide = 0;
    }
  }

  goToSlide(index: number): void {
    if (this.caseStudies.length <= 1) return;
    this.currentSlide = index;
    this.stopAutoSlide();
    this.startAutoSlide();
  }

  onMouseEnter(): void {
    this.stopAutoSlide();
  }

  onMouseLeave(): void {
    this.startAutoSlide();
  }

  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.startPosition = event.clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  onTouchStart(event: TouchEvent): void {
    this.isDragging = true;
    this.startPosition = event.touches[0].clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.touches[0].clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  redirectToAbout(): void {
    this.router.navigate(['/about-us']);
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.startAutoSlide();
  }
  injectSchemaMarkup(): void {
    const edtechSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/industries/edtech#webpage",
          "url": "https://www.kalpitatechnologies.com/industries/edtech",
          "name": "EdTech Software Development | Custom LMS & AI Learning Platforms | Kalpita Technologies",
          "description": "Next-gen EdTech software development. Custom LMS, eLearning platforms, and AI-powered adaptive learning solutions for schools, universities, and EdTech startups.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/industries/edtech#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com" },
            { "@type": "ListItem", "position": 2, "name": "Industries", "item": "https://www.kalpitatechnologies.com/industries" },
            { "@type": "ListItem", "position": 3, "name": "EdTech", "item": "https://www.kalpitatechnologies.com/industries/edtech" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/industries/edtech#service",
          "name": "EdTech Software Development Solutions",
          "serviceType": "EdTech Software Development",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "End-to-end EdTech software development delivering custom LMS, eLearning platforms, and AI-powered learning solutions..."
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/industries/edtech#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What services do you offer for EdTech software development?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We provide custom LMS development, eLearning software development services, mobile learning app development, and AI-powered EdTech modernization."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(edtechSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  onCardClick(index: number): void {
    console.log('Clicked card', index);
  }

  getStarArray(rating: number): boolean[] {
    return Array(5).fill(false).map((_, index) => index < rating);
  }

  testimonials(): void {
    this.router.navigate(['/'], { fragment: 'testimonials' });
  }
}
